/**
 * useRouteTransition — the route/view derivation + transition vocabulary +
 * chassis register that App.vue's shell reads.
 *
 * One cohesive concern: everything the shell derives from the *current
 * route*. That spans four read-only families that all key off `route.name`:
 *
 *   - View bucket + suppression flags — `currentView`, `isDashboardRoute`,
 *     `isDataPanelRoute`, `isChassisSuppressedRoute`. These gate the Aurora
 *     mount, the chassis-spine mount, and the dock mount.
 *   - Aurora compositing ceiling — `auroraOpacityCeiling` (AK-W6-α;
 *     A3 §6.R-9 / G-AK-D11): quiet-content routes recede the drift to 0.5.
 *   - Route-level pane-slide directional Transition — `routeTransitionName`
 *     + `transitionKey` (AJ-W2-δ). `routeTransitionName` is a PURE reader of
 *     `previousRouteName`, which is updated as a SIDE EFFECT in the
 *     `router.afterEach` hook (per spec §4.3 open Q #2 +
 *     RISK-ROUTE-TRANSITION-DOUBLE-COMPUTE — the reader may be invoked
 *     multiple times per render, so it must never write the ref).
 *   - Chassis register — `chassisPhase` + `chassisVariant`: the App-level
 *     `<InstrumentChassis>` cascade owner derives its `data-phase` tint and
 *     its 8th-tier housing variant from `currentView` (+ the speedtest
 *     store for the dial route's live phase).
 *
 * The composable is pure derivation + one router lifecycle hook; it owns no
 * mounted-DOM state and provides nothing, so it composes cleanly in App.vue's
 * setup and is exercised transitively by the App-level mount tests.
 */

import { computed, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import type {
    InstrumentChassisPhase,
    InstrumentChassisVariant,
} from "@mkbabb/glass-ui/instrument-chassis";
import { useSpeedtestStore } from "@features/speedtest";

/** Map route names onto the dock's view bucket. */
export type AppView = "speedtest" | "survey" | "dashboard" | "thankyou";

/**
 * DDR (AO-T1): single source of truth for route-name → AppView mapping.
 *
 * WHAT: Pure function exported at module scope so any composable that needs
 *   the bucket — `useRouteTransition().currentView` (here) AND
 *   `useAppNavigation`'s internal `currentView` — derives from ONE mapping.
 *
 * WHY: Pre-AO this mapping was duplicated in `useAppNavigation` and inlined
 *   inside this composable's `currentView` computed. Two-site drift risk:
 *   a new route variant would have required two edits and silent divergence
 *   was possible (A3 §1 §99 T1). Exporting the PURE FUNCTION (rather than
 *   the closure-bound `currentView` ComputedRef) keeps the dedup gestalt-
 *   correct: `useAppNavigation` does not need to invoke `useRouteTransition()`
 *   (which would double-register the `router.afterEach` hook below).
 *
 * RE-LITIGATE-IF: the AppView union ever grows a non-pure axis (e.g. a
 *   bucket that depends on store state or route params beyond `name`), at
 *   which point a `computed` projection is the right shape and this PURE
 *   function should be retired in favor of a `currentView` `ComputedRef`
 *   exported from a route-policy module.
 */
export function routeToView(
    routeName: string | symbol | null | undefined,
): AppView {
    if (typeof routeName !== "string") return "speedtest";
    if (routeName === "survey") return "survey";
    if (routeName === "thankyou") return "thankyou";
    if (routeName.startsWith("dashboard") || routeName.startsWith("admin"))
        return "dashboard";
    return "speedtest";
}

/** Minimal shape the Transition helpers read off a RouterView's route. */
interface ViewRouteLike {
    path: string;
    name?: string | symbol | null | undefined;
}

export function useRouteTransition() {
    const route = useRoute();
    const router = useRouter();
    const speedtestStore = useSpeedtestStore();

    const currentView = computed<AppView>(() => routeToView(route.name));

    const isDashboardRoute = computed(() => currentView.value === "dashboard");

    /** The data-panel routes — the genuine dashboard surfaces that recede
     *  behind their own opaque `bg-background` chassis (`AdminDashboardLayout`
     *  / the public dashboard layout). The Aurora is suppressed on exactly
     *  these. `/admin/login` is deliberately excluded: it is the admin
     *  auth-entry *hero* — a single floating glass card with no opaque
     *  layout chassis of its own — so it keeps the Aurora backdrop the rest
     *  of the flow routes carry. The dock stays gated on `isDashboardRoute`
     *  (the login route is not a flow station and shows no Back/Start dock). */
    const isDataPanelRoute = computed(
        () => isDashboardRoute.value && route.name !== "admin-login",
    );

    /** Chassis suppression — broader than Aurora suppression. The chassis is
     *  the cascade + housing owner; admin-login opts OUT per G-AL-D2 Path A
     *  (T7 admin-login narrative-fit) because auth is its own register, not
     *  a journey phase. The Aurora stays on at /admin/login (still NOT in
     *  `isDataPanelRoute`) so the floating glass card reads against the
     *  moving backdrop — only the chassis register suppresses here. */
    const isChassisSuppressedRoute = computed(
        () => isDataPanelRoute.value || route.name === "admin-login",
    );

    /** Per-route Aurora compositing ceiling (AK-W6-α; A3 §6.R-9 / G-AK-D11).
     *
     *  The speedtest dial is *content-over-aurora* by deliberate design — the
     *  meter is the brand register and the drift is the brand. Survey,
     *  thankyou, and admin-login are also content-over-aurora but with the
     *  opposite intent: forms / consent copy / auth chrome need legibility,
     *  and at full saturation the drift competes with the content rather than
     *  carrying it. The glass-ui `<Aurora>` exposes `:opacity-ceiling` as the
     *  outer compositing envelope (distinct from the shader's per-pixel
     *  `alpha`); quiet-content routes opt in to `0.5` so the drift recedes
     *  behind the form/text density without altering the painted image.
     *
     *  Dashboard routes don't mount Aurora at all (`isDataPanelRoute`); the
     *  default `1.0` here is therefore inert on those branches and stays
     *  unchanged so future opt-ins land at a known baseline. */
    const auroraOpacityCeiling = computed(() => {
        const name = route.name;
        if (
            name === "survey" ||
            name === "thankyou" ||
            name === "admin-login"
        ) {
            return 0.5;
        }
        return 1;
    });

    /** Group layout routes so the shell doesn't re-animate switching between
     *  child views (e.g. admin/overview → admin/data).
     *
     *  DDR-AV-ROUTE (obligation #7) — STRUCTURE-ONLY continuity. The running
     *  (`speedtest`) and result (`speedtest-result`) routes are the SAME
     *  engine state machine viewed at two URLs; they SHARE one transition key
     *  (`/speedtest`) so the route-level `<Transition>` does NOT remount the
     *  view across the running↔result state change — the in-component morph /
     *  `data-engine-state` flip stays the motion owner (W19 lands the route
     *  motion on top per DDR-AV-W19-UNGATE; this wave is structure-only). A
     *  remount here would re-create the `TraditionalSpeedtest` subtree and
     *  break engine-state continuity (the RE-LITIGATE-IF trigger). Each
     *  variant collapses to this one key because the variant is a param on the
     *  same record family, not a distinct view — `/school` and `/school/result`
     *  are the same continuous surface. */
    function transitionKey(viewRoute: ViewRouteLike): string {
        const name = typeof viewRoute.name === "string" ? viewRoute.name : "";
        if (name === "speedtest" || name === "speedtest-result") {
            return "/speedtest";
        }
        if (name.startsWith("admin")) return "/admin";
        if (name.startsWith("dashboard")) return "/dashboard";
        return viewRoute.path;
    }

    // ── AJ-W2-δ — route-level pane-slide directional Transition ──────────
    //
    // The journey is `speedtest → survey → thankyou`. The RouterView's
    // <Transition> reads a REACTIVE name from `routeTransitionName(viewRoute)`:
    //   • forward through the journey (index grew)   → `pane-slide`
    //   • backward through the journey (index shrank) → `pane-slide-back`
    //   • either end off-journey (dashboard/admin/catch-all/same idx) → `fade`
    //
    // `previousRouteName` is updated as a SIDE-EFFECT at the router lifecycle
    // hook (`router.afterEach`) rather than inside the computed (per spec §4.3
    // open Q #2 + RISK-ROUTE-TRANSITION-DOUBLE-COMPUTE — Vue may invoke the
    // reader multiple times per render; mutating state inside the reader would
    // store the wrong previous-route name). The function is a PURE READER:
    // it observes the ref, never writes it.
    //
    // The speedtest route family (`speedtest` running + `speedtest-result`
    // complete, across every `:variant` param) all collapses to ONE journey
    // index = 0 via the `journeyIndex` mapping function (per spec §4.3 open
    // Q #3 + RISK-VARIANT-ROUTE-COLLAPSE) — navigating between the running and
    // result states (or between variant params) reads as a same-index swap
    // (→ `fade`), not as a directional page-turn. DDR-AV-ROUTE collapsed the
    // four flat variant records to one parameterized group, so the family is
    // now name-stable (`speedtest`/`speedtest-result`) + param-keyed.

    /** Map a route name to its journey index. Returns -1 for routes that
     *  are not on the speedtest→survey→thankyou journey (dashboard, admin,
     *  catch-all). The `startsWith("speedtest")` clause collapses the whole
     *  speedtest family (`speedtest` running + `speedtest-result` complete,
     *  every variant param) to ONE index = 0 so a running → result swap reads
     *  as a same-index fade, not as a directional page-turn. */
    function journeyIndex(name: string): number {
        if (name.startsWith("speedtest")) return 0;
        if (name === "survey") return 1;
        if (name === "thankyou") return 2;
        return -1;
    }

    const previousRouteName = ref<string | null>(null);

    /** Compute the Transition name for the entering route. Pure reader of
     *  `previousRouteName` (updated in `router.afterEach` below). */
    function routeTransitionName(viewRoute: {
        name?: string | symbol | null | undefined;
    }): string {
        const currentName =
            typeof viewRoute.name === "string" ? viewRoute.name : "";
        const prevName = previousRouteName.value ?? "";

        const currentIdx = journeyIndex(currentName);
        const prevIdx = journeyIndex(prevName);

        // Either end off-journey: keep the canonical fade (dashboard tab
        // swaps, admin-login, catch-all, initial mount with no prev).
        if (currentIdx === -1 || prevIdx === -1) return "fade";

        // Forward through the journey (index grew): pane-slide.
        if (currentIdx > prevIdx) return "pane-slide";

        // Backward through the journey (index shrank): pane-slide-back.
        if (currentIdx < prevIdx) return "pane-slide-back";

        // Same index (e.g. between speedtest variants): fade.
        return "fade";
    }

    // Side-effect: capture the FROM route's name on every navigation so the
    // next render's `routeTransitionName` reader can compute direction. The
    // `afterEach` hook fires AFTER navigation commits, so by the time the
    // RouterView re-renders with the new component, `previousRouteName`
    // reflects the FROM (= previous) route's name. ✓
    router.afterEach((_to, from) => {
        previousRouteName.value =
            typeof from.name === "string" ? from.name : null;
    });

    /** Chassis phase — drives the `data-phase` cascade on the App-level
     *  `<InstrumentChassis>` spine. I derive the phase from `currentView`
     *  + the speedtest store so the three journey stations share one
     *  persistent chassis frame across `speedtest → survey → thankyou`:
     *
     *    - speedtest route → reads `speedtestStore.currentPhase`
     *      (the 5-state chassis vocabulary `ready | ping | download |
     *      upload | complete` already lives at `features/speedtest/state/phase.ts`).
     *      Covers the whole speedtest route family (`speedtest` running +
     *      `speedtest-result` complete, every `:variant` param) via
     *      `currentView`'s `name.startsWith("speedtest")` clause.
     *    - survey / thankyou routes → pinned to `"complete"`. By the time
     *      the user reaches these stations the test has finished and the
     *      chassis tint should read as the journey terminus. Binding to
     *      the store directly would be coupled to mutability (a retake
     *      from survey would retint mid-survey); explicit `complete` keeps
     *      the journey-terminus reading invariant.
     *    - dashboard / admin routes → the chassis is not mounted at all
     *      (`v-if="!isDataPanelRoute"` above), so this branch returns
     *      `undefined` and the chassis falls back to the primitive's
     *      `ready` default if it were ever mounted on a non-journey route.
     *
     *  The primitive resolves `phase ?? 'ready'` at its template root, so
     *  returning `undefined` here is the canonical "no phase yet" reading. */
    const chassisPhase = computed<InstrumentChassisPhase | undefined>(() => {
        const view = currentView.value;
        if (view === "speedtest") return speedtestStore.currentPhase;
        if (view === "survey" || view === "thankyou") return "complete";
        return undefined;
    });

    /** Chassis variant — selects the 8th tier housing register (AL-W1-α;
     *  glass-ui b25fb11; G-AL-D1 Path A). Quiet-content routes (survey /
     *  thankyou) opt INTO `variant="spine"` so the chassis paints a polished
     *  HOUSING sleeve — hairline outer stroke + interior radial vignette —
     *  that frames the inner card as a lifted module. The speedtest dial
     *  route keeps the default `"glass"` (omitted variant): the Aurora
     *  drift IS the brand register at that station and a competing housing
     *  bezel would clutter the canvas. Admin-login never reaches here
     *  (suppressed via `isChassisSuppressedRoute` above per G-AL-D2 Path A).
     *  Closes the AK W2 chassis-spine architectural underclaim — the App-
     *  level chassis is no longer a transparent cascade pipe on the quiet-
     *  content routes; it READS as the housing the W2 architecture promised. */
    const chassisVariant = computed<InstrumentChassisVariant>(() => {
        const view = currentView.value;
        if (view === "survey" || view === "thankyou") return "spine";
        return "glass";
    });

    return {
        currentView,
        isDashboardRoute,
        isDataPanelRoute,
        isChassisSuppressedRoute,
        auroraOpacityCeiling,
        transitionKey,
        routeTransitionName,
        chassisPhase,
        chassisVariant,
    };
}
